import { IWarehouse } from '@/modules/warehouses/ts';
import styles from './index.module.sass';
import stationarySvg from './icons/stationary.svg';
import sleeveSvg from './icons/sleeve.svg';
import classNames from 'classnames';
import { notification } from 'antd';
import { useMemo, useState } from 'react';
import { CaretUpOutlined, CaretDownOutlined } from '@ant-design/icons';

interface SortState {
  column: string | null;
  direction: 'asc' | 'desc' | null;
}

export const WarehousesList = ({
  warehouses,
  columns,
  activeWarehouse,
  onClickWarehouse,
}: {
  activeWarehouse: number | null;
  warehouses: IWarehouse[];
  columns: { title: string; accessor: string }[];
  onClickWarehouse: (id: number) => void;
}) => {
  const [sortState, setSortState] = useState<SortState>({
    column: null,
    direction: null,
  });

  const handleHeaderClick = (accessor: string) => {
    setSortState((prev) => ({
      column: accessor,
      direction: prev.column === accessor && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const sortedWarehouses = useMemo(() => {
    if (!sortState.column) return warehouses;

    return [...warehouses].sort((a, b) => {
      const aValue = a[sortState.column];
      const bValue = b[sortState.column];

      if (sortState.direction === 'asc') {
        return aValue > bValue ? 1 : -1;
      }
      return aValue < bValue ? 1 : -1;
    });
  }, [warehouses, sortState]);

  const renderSortIcon = (column: string) => {
    if (sortState.column !== column) {
      return (
        <span className={styles.sorter}>
          <span className={styles.sorter_inner}>
            <span className={styles.sorter_up}>
              <CaretUpOutlined className={styles.sortIcon} />
            </span>
            <span className={styles.sorter_down}>
              <CaretDownOutlined className={styles.sortIcon} />
            </span>
          </span>
        </span>
      );
    }

    return sortState.direction === 'asc' ? (
      <span className={styles.sorter}>
        <span className={styles.sorter_inner}>
          <span className={styles.sorter_up}>
            <CaretUpOutlined className={styles.sortIconActive} />
          </span>
          <span className={styles.sorter_down}>
            <CaretDownOutlined className={styles.sortIcon} />
          </span>
        </span>
      </span>
    ) : (
      <span className={styles.sorter}>
        <span className={styles.sorter_inner}>
          <span className={styles.sorter_up}>
            <CaretUpOutlined className={styles.sortIcon} />
          </span>
          <span className={styles.sorter_down}>
            <CaretDownOutlined className={styles.sortIconActive} />
          </span>
        </span>
      </span>
    );
  };

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.column} onClick={() => handleHeaderClick('id')}>
          № {renderSortIcon('id')}
        </div>
        <div className={styles.column} onClick={() => handleHeaderClick('name')}>
          название {renderSortIcon('name')}
        </div>
        <div
          className={classNames(styles.column, styles.weightWidth)}
          onClick={() => handleHeaderClick('totalWeight')}
        >
          вес {renderSortIcon('totalWeight')}
        </div>
      </div>
      <div className={styles.content}>
        {sortedWarehouses.map((item, i) => (
          <div
            onClick={() => item.id && onClickWarehouse(+item.id)}
            className={classNames(
              styles.contentItem,
              item.id === activeWarehouse && styles.selected,
            )}
          >
            <div className={styles.col}>{item.id}</div>
            <div className={styles.col}>
              <img
                className={styles.ItemIcon}
                src={item.type === 1 ? stationarySvg : sleeveSvg}
                alt="warehouse-type"
              />{' '}
              {item.name}
            </div>
            <div className={classNames(styles.col, styles.weightWidth)}>
              {item.totalWeight?.toLocaleString('ru', { maximumFractionDigits: 1 }) ||
                0}{' '}
              т
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
